/* eslint-disable react-native/no-inline-styles */
import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';
import * as RNLocalize from 'react-native-localize';

import { Element, scale } from '@components/frame';
import { getLocale, isPhone } from '@helpers';

import { font } from '@styles/fonts';
import { getBoxShadowStyle } from '@styles/shadow';
import Line from '@components/common/line';
import TouchableElement from '@components/common/touchableElement';
import Loading from '@components/loading';
import moment from 'moment';

import { getTimeSetup } from '@reducers/time';

import DeviceTime from './deviceTime';
import { useForceUpdate } from '@hooks/useForceUpdate';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

let interval;

const Time = (props) => {
  const {
    C,
    getTimeSetup,
    time: { loaded, syncTime, publicServer = {}, server = {} },
    time,
  } = props;

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  const forceUpdate = useForceUpdate();

  useEffect(() => {
    (async () => {
      if (!server || !server.offset) {
        await getTimeSetup();
      }
    })();
  }, []);

  useEffect(() => {
    if (loaded) {
      interval = setInterval(() => {
        forceUpdate();
      }, 100);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [loaded]);

  if (!loaded) {
    return <Loading />;
  }

  const timeValues = [
    { name: i18n.t('Time zone'), value: server.factoryTimeZoneIana, noArrow: true },
    { name: i18n.t('Time zone offset'), value: server.factoryTimeZoneOffset, noArrow: true },
    { name: i18n.t('Platform UTC time'), value: moment.utc().add(server.offset, 'milliseconds').format('HH:mm:ss'), noArrow: true },
    { name: i18n.t('Public UTC time'), value: moment.utc().add(publicServer.offset, 'milliseconds').format('HH:mm:ss'), noArrow: true },
  ];

  const timeZoneOffset = (() => {
    let offset = new Date().getTimezoneOffset();

    const sign = offset > 0 ? '-' : '';

    offset = Math.abs(offset);
    const hours = Math.floor(offset / 60);

    const min = offset - hours * 60;

    return `${sign}${hours < 10 ? `0${hours}` : hours}:${min < 10 ? `0${min}` : min}:00`;
  })();
  const deviceValues = [
    { name: i18n.t('Local time'), value: moment().format('HH:mm:ss'), noArrow: true },
    { name: i18n.t('Offset correction'), value: `${server.offset} ms`, noArrow: true },
    { name: i18n.t('Local time corrected'), value: moment().add(server.offset, 'milliseconds').format('HH:mm:ss'), noArrow: true },
    { name: i18n.t('Last sync'), value: moment(syncTime, 'HH:mm:ss').format('HH:mm:ss'), noArrow: true },
    { name: i18n.t('Time zone'), value: RNLocalize.getTimeZone(), noArrow: true },
    { name: i18n.t('Time zone offset'), value: timeZoneOffset, noArrow: true },
    { name: i18n.t('Time locale code (depends on language)'), value: getLocale(), noArrow: true },
    { name: i18n.t('Time format'), value: moment().format('LLLL'), noArrow: true },
  ];

  return (
    <ScrollView style={{ marginTop: scale(144) }}>
      <View
        style={{
          width: '100%',
          paddingBottom: scale(50),
          height: scale(750),
        }}
      >
        <Element
          {...{
            config: {
              position: {
                top: 0,
                right: 180,
                left: 180,
              },
            },
            configMobile: {
              position: {
                top: 0,
                right: 0,
                left: 0,
              },
            },
          }}
        >
          <View style={{ paddingLeft: scale(15) }}>
            <Text style={[font.Headline, { color: C('Label/Dark/Primary') }]}>Your Factory</Text>
          </View>
          <Element
            {...{
              config: {
                position: {
                  left: 0,
                  right: 0,
                  top: 28,
                },
                height: 248,
              },
            }}
          >
            <View
              style={[
                shadowStyles,
                {
                  width: '100%',
                  borderRadius: scale(SCREEN_BORDER_RADIUS),
                },
              ]}
            >
              <TouchableElement onPress={() => {}}>
                {({ pressHoverColor, boxStyle }) => (
                  <View
                    style={[
                      boxStyle,
                      {
                        borderBottomColor: C('Hairline / Light / No trans'),
                        height: scale(88),
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexDirection: 'row',
                        borderBottomWidth: StyleSheet.hairlineWidth,
                        paddingLeft: scale(15),
                        borderTopLeftRadius: isPhone ? 0 : scale(SCREEN_BORDER_RADIUS),
                        borderTopRightRadius: isPhone ? 0 : scale(SCREEN_BORDER_RADIUS),
                        backgroundColor: C(pressHoverColor('Background / Light / Field')),
                      },
                    ]}
                  >
                    <View>
                      <View>
                        <Text
                          style={[
                            {
                              color: C(pressHoverColor('Label / Light / Primary')),
                            },
                            font.Body,
                          ]}
                        >
                          Local time
                        </Text>
                      </View>
                      <View>
                        <DeviceTime {...{ C, timeOffset: server.offset }} />
                      </View>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <TouchableOpacity onPress={getTimeSetup} activeOpacity={1}>
                        <View style={{ paddingRight: scale(12) }}>
                          <Text
                            style={[
                              {
                                color: C('Link/Dark/Primary'),
                              },
                              font.Body,
                            ]}
                          >
                            Resync
                          </Text>
                        </View>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </TouchableElement>
              {timeValues.map((item, index) => (
                <Line {...{ item, index, array: timeValues, C, roundBottom: index === timeValues.length - 1 }} key={item.name} />
              ))}
            </View>
          </Element>
        </Element>
        <Element
          {...{
            config: {
              position: {
                top: 310,
                right: 180,
                left: 180,
              },
            },
            configMobile: {
              position: {
                top: 320,
                right: 0,
                left: 0,
              },
            },
          }}
        >
          <View style={{ paddingLeft: scale(15) }}>
            <Text style={[font.Headline, { color: C('Label/Dark/Primary') }]}>Your Device</Text>
          </View>
          <Element
            {...{
              config: {
                position: {
                  left: 0,
                  right: 0,
                  top: 28,
                },
                height: 248,
              },
            }}
          >
            <View
              style={[
                shadowStyles,
                {
                  width: '100%',
                  borderRadius: scale(SCREEN_BORDER_RADIUS),
                },
              ]}
            >
              {deviceValues.map((item, index) => (
                <Line
                  {...{ item, index, array: deviceValues, C, roundTop: index === 0, roundBottom: index === deviceValues.length - 1 }}
                  key={item.name}
                />
              ))}
            </View>
          </Element>
        </Element>
      </View>
    </ScrollView>
  );
};

export default connect(({ time }) => ({ time }), { getTimeSetup })(Time);
